"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";

function RegisterForm() {
  const searchParams = useSearchParams();
  const flow = searchParams.get("flow"); // 'asap' or 'schedule'

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    document_number: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || "https://testapp.exeinformatica.com.ar/api";
      const res = await fetch(`${apiUrl}/register/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Ocurrió un error al registrarse");
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex flex-col justify-center items-center p-6 relative overflow-hidden">
        <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 p-8 text-center relative z-10">
          <div className="text-6xl mb-4">✉️</div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">Revisá tu correo</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            Te hemos enviado un correo a <strong>{formData.email}</strong>. Hacé clic en el enlace para verificar tu cuenta y poder iniciar sesión.
          </p>
          <Link href="/login" className="bg-primary text-primary-foreground py-3 px-6 rounded-xl font-bold hover:bg-blue-700 transition-all">
            Ir a Iniciar Sesión
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col justify-center items-center p-6 relative overflow-hidden">
      {/* Background gradients */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>

      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 p-8 relative z-10">
        <div className="text-center mb-8">
          <Link href="/" className="text-2xl font-bold text-primary tracking-tight">
            Doctor<span className="text-slate-800 dark:text-white">Online</span>
          </Link>
          <h2 className="text-xl font-semibold mt-6 text-slate-800 dark:text-white">
            {flow === "asap" ? "Pedir médico ahora" : "Crear tu cuenta"}
          </h2>
          <p className="text-slate-500 text-sm mt-2">
            Completá tus datos para acceder al consultorio virtual.
          </p>
        </div>

        <form className="space-y-5" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-3 rounded-lg text-sm text-center">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nombre</label>
              <input type="text" name="first_name" required onChange={handleChange} value={formData.first_name} className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all" placeholder="Juan" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Apellido</label>
              <input type="text" name="last_name" required onChange={handleChange} value={formData.last_name} className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all" placeholder="Pérez" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email</label>
            <input type="email" name="email" required onChange={handleChange} value={formData.email} className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all" placeholder="juan@ejemplo.com" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Número de Documento</label>
            <input type="text" name="document_number" required onChange={handleChange} value={formData.document_number} className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all" placeholder="Sin puntos" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Contraseña</label>
            <input type="password" name="password" required onChange={handleChange} value={formData.password} className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all" placeholder="••••••••" />
          </div>

          <button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground py-3.5 rounded-xl font-bold text-lg shadow-lg shadow-blue-500/30 hover:bg-blue-700 transition-all mt-4 disabled:opacity-50">
            {loading ? "Registrando..." : "Continuar"}
          </button>
        </form>

        <p className="text-center text-sm text-slate-500 mt-8">
          ¿Ya tenés cuenta? <Link href="/login" className="text-primary font-semibold hover:underline">Iniciá sesión</Link>
        </p>
      </div>
    </div>
  );
}

export default function RegisterPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center">Cargando...</div>}>
      <RegisterForm />
    </Suspense>
  );
}
